import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { InfoPage as InfoPageData, SiteSettings } from '@/types'

import Link from 'next/link'
import RichText from '@/components/rich-text'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SaSlides } from '@/components/generals/sa-slides'
import { Cms } from '@/components/generals/cms'
import { useT } from '@/lib/i18n'
import { buttonClass } from '@/lib/ui'
import { getPage, getSettings, safely } from '@/lib/shop/data'

interface InfoPageProps {
    page: InfoPageData
    settings: SiteSettings | null
}

const InfoPage: FC<InfoPageProps> = ({ page, settings }): ReactNode => {
    const t = useT()

    return (
        <Layout settings={settings}>
            <Seo title={page.title.v} description={page.intro.v} ogImage={page.image || undefined} />

            <section id='page-header' className='sa-photo border-b-2 border-line bg-sky'>
                <SaSlides start='blyde' />
                <div className='mx-auto max-w-4xl px-4 py-14 sm:px-6'>
                    <Cms as='h1' table='pages' id={page.id} field={page.title} className='text-5xl sm:text-6xl' />
                    {page.intro.v && <Cms as='p' table='pages' id={page.id} field={page.intro} className='mt-4 text-xl' />}
                </div>
            </section>

            <section id='page-content' className='mx-auto max-w-4xl px-4 py-14 sm:px-6'>
                {page.image && (
                    <img src={page.image} alt={page.imageAlt} className='mb-10 aspect-[16/9] w-full rounded-3xl border-2 border-line object-cover shadow-pop-lg' />
                )}

                <RichText html={page.body} className='text-lg leading-relaxed' />

                {page.slug === 'about' && (
                    <div className='mt-10 flex flex-wrap gap-4'>
                        <Link href='/shop' className={buttonClass('pink')}>{t('navShop')}</Link>
                        <Link href='/contact' className={buttonClass('yellow')}>{t('navContact')}</Link>
                    </div>
                )}
            </section>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

export const getStaticProps: GetStaticProps<InfoPageProps> = async ({ params, locale }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const [page, settings] = await Promise.all([
        safely(() => getPage(slug, locale), null),
        safely(() => getSettings(locale), null)
    ])

    if (!page) return { notFound: true, revalidate: 30 }

    return { props: { page, settings }, revalidate: 30 }
}

export default InfoPage
